import React, { Fragment, useRef, useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import { connect } from 'react-redux';

import styles from './QQLoginPieChartTotal.module.css';
import { createBuckets } from './helpers';
import { LOGIN_SLICE_NAMES } from '../../../../constants';
import {
  getLoginBucketsPieA11yLabel,
  preventDescendantFocus,
} from '../../../../utils';

const COLORS = ['#17758e', '#a2d6dd', '#c8c8c8', '#fcc8d8', '#da0652'];
const MIN_GRAPHIC_WIDTH = 420;
const CARD_INFO_HEIGHT = 260;
const PADDING_ANGLE = 2;

const legendWrapperStyle = {
  top: 0,
  left: '50%',
  display: 'flex',
  alignItems: 'center',
  height: '100%',
};

const CustomTooltip = ({ payload }) => {
  const payloadData = get(payload, '[0]');
  if (payloadData) {
    const { name, value } = payloadData;

    const legend = payload && `${value} students logged in ${name} times`;
    return (
      <div className={styles.customTooltip}>
        <div className={styles.customTooltipLegend}>{legend}</div>
      </div>
    );
  }
  return null;
};

CustomTooltip.propTypes = {
  payload: PropTypes.array,
};

const RenderLegend = ({ payload }) => (
  <div className={styles.chartWrapper}>
    {payload &&
      payload.map((d, i) => {
        const {
          payload: { name, value },
        } = d;

        return (
          <div className={styles.legend} key={name}>
            <div
              className={styles.legendPoint}
              style={{ background: COLORS[i] }}
            />
            <span className={styles.value}>{value} </span>
            students logged in
            <span className={styles.value}> {name} times</span>
          </div>
        );
      })}
  </div>
);

RenderLegend.propTypes = {
  payload: PropTypes.array,
};

function QQLoginPieChartTotal({ data, containerWidth }) {
  const loginValues =
    Array.isArray(data.insightsData) &&
    data.insightsData.find((d) => d.name === LOGIN_SLICE_NAMES.LOGIN_DATA);

  const logins =
    Array.isArray(loginValues.value) &&
    loginValues.value.map((x) => x.launches);
  const loginBuckets = createBuckets(logins);

  // Serialize loginData
  const loginData = Array.isArray(loginBuckets)
    ? loginBuckets
        .map((x) => ({
          value: x.logins,
          name: x.label,
        }))
        .reverse()
    : [];

  const [width, setWidth] = useState(0);
  const elementRef = useRef(null);

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    if (rect.width) setWidth(rect.width);
  }, [elementRef.current, containerWidth]);

  const SHOW_ONLY_LEGEND = width < MIN_GRAPHIC_WIDTH;

  const chartAltText = getLoginBucketsPieA11yLabel(loginData);

  return (
    <div
      ref={elementRef}
      className={
        SHOW_ONLY_LEGEND ? styles.onlyLegendContainer : styles.widgetContainer
      }
    >
      {!SHOW_ONLY_LEGEND && (
        <Fragment>
          <div
            role="img"
            aria-label={chartAltText}
            onMouseDown={preventDescendantFocus}
          >
            <ResponsiveContainer height={CARD_INFO_HEIGHT}>
              <PieChart aria-hidden="true">
                <Pie
                  rootTabIndex={-1}
                  data={loginData || null}
                  cx="25%"
                  cy="50%"
                  innerRadius={'50%'}
                  outerRadius={'80%'}
                  paddingAngle={PADDING_ANGLE}
                  dataKey="value"
                >
                  {loginData.map((d, i) => (
                    <Cell key={d.name} fill={COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  content={<RenderLegend />}
                  layout="vertical"
                  verticalAlign="middle"
                  wrapperStyle={legendWrapperStyle}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className={styles.totalInfo}>
            <p>
              <b>{logins.length}</b> total students
            </p>
          </div>
        </Fragment>
      )}
      {SHOW_ONLY_LEGEND && (
        <RenderLegend
          payload={
            loginData &&
            loginData.map(({ name, value }) => ({ payload: { name, value } }))
          }
        />
      )}
    </div>
  );
}

QQLoginPieChartTotal.propTypes = {
  data: PropTypes.object,
  containerWidth: PropTypes.number,
};

function mapStateToProps(state) {
  const containerWidth = get(state, 'insights.containerWidth', 0);

  return {
    containerWidth,
  };
}

export default connect(mapStateToProps)(QQLoginPieChartTotal);
